import { React, useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import { getFirebaseError } from "../services/error-codes"
import { AuthContext } from "../contexts/AuthContext"
import ComponentLoader from "../components/ComponentLoader"
import { CommonContext } from "../contexts/CommonContext"
import {
  getUiProductsData,
  getUserPilotOrders,
  getUserProductOrders,
  getUserProfileData,
  logAction,
} from "../services/api"
import { Paper, Modal } from "@mui/material"
import NavHeader from "../components/NavHeader"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import CloseIcon from "@mui/icons-material/Close"

const styles = {
  shadowBox: {
    background: "white",
    padding: "10px",
    marginTop: "10px",
  },
  modalStyle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '550px',
    maxHeight: '90vh',
    overflow: 'auto',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 3,
  },
}

function Profile() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const { getUserId, getCustomerId } = useContext(AuthContext)
  const [profileData, setProfileData] = useState(null)
  const [orders, setOrders] = useState([])
  const [displayedOrdersCount, setDisplayedOrdersCount] = useState(5)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { logout } = useContext(AuthContext)
  const { showLoader, hideLoader, showAlert, showSnackbar } =
    useContext(CommonContext)
  const [specifications, setSpecifications] = useState({})

  useEffect(() => {
    logAction("PAGE_VIEW", "profile")
    getUserProfile()
  }, [])

  const getUserProfile = async () => {
    getUserProfileData(await getUserId())
      .then((response) => {
        setProfileData(response)
        if (!response) {
          navigate("/auth")
        }
        getUserOrders()
      })
      .catch((error) => {
        setLoading(false)
        showAlert(getFirebaseError(error))
      })
  }

  const getUserOrders = async () => {
    const userData = {
      customerId: await getCustomerId(),
      userId: await getUserId(),
    }

    getUserPilotOrders(userData)
      .then((response) => {
        console.log("getUserProduct PILOT Orders response", response)
        
        // Handle the new API response format
        if (response.success && response.orders) {
          // Sort orders by timestamp descending (newest first)
          const sortedOrders = response.orders.sort((a, b) => {
            const timeA = a.timeStamp || new Date(a.createdAt).getTime()
            const timeB = b.timeStamp || new Date(b.createdAt).getTime()
            return timeB - timeA
          })
          setOrders(sortedOrders)
        } else {
          setOrders([])
        }
        setLoading(false)
      })
      .catch((error) => {
        console.error("Error fetching orders:", error)
        setLoading(false)
        showAlert(
          "Unexpected error occured. For any issues/enquiries please contact customer care"
        )
      })
  }

  const handleLoadMore = () => {
    setDisplayedOrdersCount(prev => prev + 5)
  }

  const handleOrderClick = (order) => {
    setSelectedOrder(order)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedOrder(null)
  }

  const renderCustomizations = (customizations) => {
    if (!Object.keys(customizations).length) return null

    return Object.entries(customizations).map(([key, customization]) => {
        if (key  == 'additional_customization' && !customization.selections) { 
          return null
        }
        return (
          <Box key={key} sx={{ marginBottom: "8px" }}>
            <Box sx={{ fontSize: "11px", marginLeft: "10px", background:'#eaeaea', padding:'5px', borderRadius:'5px' }}>
              {customization.name || customization.optionName}
              {parseFloat(customization.additionalPrice) > 0 && (
                <span style={{ color: "#a4243d" }}> (+₹{customization.additionalPrice})</span>
              )}
            </Box>
          </Box>
        )
    })
  }

  const displayedOrders = orders.slice(0, displayedOrdersCount)
  const hasMore = displayedOrdersCount < orders.length

  return (
    <Box sx={{ padding: "4vw", maxWidth: "550px", marginTop: "7vh" }}>
      {loading ? (
        <ComponentLoader />
      ) : (
        <Box>
          <NavHeader />
          <Box sx={{ fontSize: "25px", fontFamily: "Foregen" }}>PROFILE</Box>
          <Box style={styles.shadowBox}>
            <Box
              sx={{ display: "flex", flexDirection: "column", padding: "10px" }}
            >
              <Box
                sx={{
                  marginBottom: "5px",
                  fontSize: "25px",
                  fontWeight: "bold",
                  textTransform: "capitalize",
                }}
              >
                {profileData.f_name}
              </Box>
              <Box sx={{ marginBottom: "5px" }}>{profileData.phone}</Box>
              <Box
                sx={{
                  marginBottom: "5px",
                  border: "1px solid #bfbfbf",
                  padding: "10px",
                  margin: "10px 0",
                  borderRadius: "5px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
                onClick={() => navigate("/myAddresses")}
              >
                My Addresses
                <ChevronRightIcon />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", marginTop: "20px" }}>
            <Box>
              <Button variant="outlined" onClick={() => logout()}>
                Logout
              </Button>
            </Box>
          </Box>

          <Box
            sx={{ fontSize: "25px", fontFamily: "Foregen", marginTop: "25px" }}
          >
            Your Order's
          </Box>
          <Box>
            {orders.length ? (
              <Box>
                {displayedOrders.map((item, index) => {
                  return (
                    <Paper
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        margin: "10px 0",
                        padding: "10px",
                      }}
                      key={index}
                    >
                      <Box mb={1} sx={{ fontWeight: "bold" }}>
                        {item.products && item.products.length > 0
                          ? item.products[0]?.name.charAt(0).toUpperCase() +
                            item.products[0]?.name.substr(1).toLowerCase()
                          : "Order"}
                      </Box>
                      <Box mb={1} sx={{ fontWeight: "bold" }}>
                        ₹ {item.total - item.discount}
                      </Box>
                      <Box mb={1} sx={{ fontSize: "10px" }}>
                        {new Date(item.createdAt).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </Box>
                      <Box mb={1} sx={{ textTransform: "capitalize" }}>
                        Order ID : {item.orderId}
                      </Box>
                      <Box
                        mb={1}
                        sx={{ textTransform: "capitalize", color: "#a4243d" }}
                      >
                        Status : {item.order_status}
                      </Box>
                      <Box
                        sx={{
                          textAlign: "right",
                          borderTop: "1px solid #bfbfbf",
                          paddingTop: "10px",
                          color: "#a4243d",
                          cursor: "pointer",
                        }}
                        onClick={() => handleOrderClick(item)}
                      >
                        {item.order_status == "delivered"
                          ? "View Order"
                          : "Track Order"}
                      </Box>
                    </Paper>
                  )
                })}
                {hasMore && (
                  <Box sx={{ textAlign: "center", marginTop: "20px" }}>
                    <Button
                      variant="outlined"
                      onClick={handleLoadMore}
                      sx={{
                        borderColor: "#a4243d",
                        color: "#a4243d",
                        "&:hover": {
                          borderColor: "#a4243d",
                          backgroundColor: "#a4243d",
                          color: "white",
                        },
                      }}
                    >
                      Load More
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Paper
                sx={{
                  padding: "20PX 0",
                  textAlign: "center",
                  marginTop: "10px",
                }}
              >
                No orders found
              </Paper>
            )}
          </Box>

          {/* Order Details Modal */}
          <Modal
            open={isModalOpen}
            onClose={handleCloseModal}
            aria-labelledby="order-details-modal"
            aria-describedby="order-details-description"
          >
            <Box sx={styles.modalStyle}>
              {selectedOrder && (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "20px",
                    }}
                  >
                    <Box sx={{ fontSize: "20px", fontWeight: "bold" }}>
                      Order Details
                    </Box>
                    <CloseIcon
                      sx={{ cursor: "pointer" }}
                      onClick={handleCloseModal}
                    />
                  </Box>

                  {/* Order Items */}
                  <Paper sx={{ padding: "15px", marginBottom: "15px" }}>
                    <Box sx={{ display: "flex", fontWeight: "bold" }} mb={1}>
                      <Box sx={{ width: "50%" }}>Item Name</Box>
                      <Box sx={{ width: "15%" }}>Price</Box>
                      <Box sx={{ width: "15%", textAlign: "center" }}>Qty</Box>
                      <Box sx={{ width: "20%", textAlign: "right" }}>Total</Box>
                    </Box>

                    {selectedOrder.products &&
                      selectedOrder.products.map((item, index) => {
                        return (
                          <Box key={index} sx={{ marginBottom: "15px", borderBottom: "1px solid #f0f0f0", paddingBottom: "10px" }}>
                            <Box sx={{ display: "flex" }} mb={1}>
                              <Box sx={{ width: "50%", textTransform: "capitalize", fontWeight: "bold" }}>
                                {item.name}
                              </Box>
                              <Box sx={{ width: "15%" }}>₹{item.price}</Box>
                              <Box sx={{ width: "15%", textAlign: "center" }}>
                                {item.quantity}
                              </Box>
                              <Box sx={{ width: "20%", textAlign: "right" }}>
                                ₹ {item.quantity * item.price}
                              </Box>
                            </Box>
                            
                            {/* Product Variant */}
                            {item.variant && (
                              <Box sx={{ marginBottom: "8px", marginLeft: "10px" }}>
                                <Box sx={{ fontSize: "12px", color: "#666" }}>
                                  <strong>Quantity:</strong> {item.variant.weight}
                                </Box>
                              </Box>
                            )}

                            {/* Product Customizations */}
                            {item.customizations && (
                              <Box sx={{  marginTop: "8px", display:'flex' }}>
                                {renderCustomizations(item.customizations)}
                              </Box>
                            )}
                          </Box>
                        )
                      })}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                      mt={2}
                      pt={2}
                    >
                      <Box>Subtotal</Box>
                      <Box>₹ {selectedOrder.subtotal}</Box>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }} mt={1}>
                      <Box>Shipping Cost</Box>
                      <Box>₹ {selectedOrder.shippingCost}</Box>
                    </Box>
                    {selectedOrder.discount > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }} mt={1}>
                        <Box>Discount</Box>
                        <Box>- ₹ {selectedOrder.discount}</Box>
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontWeight: "bold",
                        borderTop: "1px solid #eaeaea",
                      }}
                      mt={2}
                      pt={2}
                    >
                      <Box>Total</Box>
                      <Box>₹ {selectedOrder.total - selectedOrder.discount}</Box>
                    </Box>
                  </Paper>

                  {/* Order Information */}
                  <Paper sx={{ padding: "15px", fontSize: "17px" }}>
                    <Box sx={{ display: "flex", marginBottom: "5px" }}>
                      <Box sx={{ width: "40%" }}>Order ID</Box>
                      <Box>: {selectedOrder.orderId}</Box>
                    </Box>
                    <Box sx={{ display: "flex", marginBottom: "5px" }}>
                      <Box sx={{ width: "40%" }}>Order Date</Box>
                      <Box>
                        :{" "}
                        {new Date(selectedOrder.createdAt).toLocaleDateString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          }
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", marginBottom: "10px" }}>
                      <Box sx={{ width: "40%" }}>Order Status</Box>
                      <Box sx={{ textTransform: "capitalize", color: "#a4243d" }}>
                        : {selectedOrder.order_status}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", marginBottom: "10px" }}>
                      <Box sx={{ width: "40%" }}>Payment Mode</Box>
                      <Box>
                        :{" "}
                        {selectedOrder.paymentMethod === "cash_on_delivery"
                          ? "Cash on delivery"
                          : "Online"}
                      </Box>
                    </Box>

                    {/* Delivery Address */}
                    {selectedOrder.deliveryAddress && (
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Box sx={{ width: "60%", marginTop: "10px" }}>
                          Delivery Address:
                        </Box>
                        <Box sx={{ width: "100%", marginTop: "2px" }}>
                          {selectedOrder.deliveryAddress.userName && `${selectedOrder.deliveryAddress.userName}, `}
                          {selectedOrder.deliveryAddress.houseDetails && `${selectedOrder.deliveryAddress.houseDetails}, `}
                          {selectedOrder.deliveryAddress.streetDetails && `${selectedOrder.deliveryAddress.streetDetails}, `}
                          {selectedOrder.deliveryAddress.landmark && `${selectedOrder.deliveryAddress.landmark}, `}
                          {selectedOrder.deliveryAddress.pincode && `${selectedOrder.deliveryAddress.pincode}`}
                        </Box>
                      </Box>
                    )}

                    {/* Delivery Person Info */}
                    {selectedOrder.deliveryPersonName && (
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <Box>Delivery Partner Assigned:</Box>
                        <Box>{selectedOrder.deliveryPersonName}</Box>
                        {selectedOrder.deliveryPersonMobile && (
                          <Box>{selectedOrder.deliveryPersonMobile}</Box>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}
            </Box>
          </Modal>
        </Box>
      )}
    </Box>
  )
}

export default Profile
